import React from 'react';

import { defineMessages, type MessageDescriptor } from 'react-intl';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import type { InsertSnippetInputMethod } from '@atlaskit/editor-common/analytics/types/insert-events';
import { logException } from '@atlaskit/editor-common/monitoring';
import type {
	QuickInsertActionInsert,
	QuickInsertItem,
} from '@atlaskit/editor-common/provider-factory';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Snippet } from '@atlassian/editor-snippets-provider/types';

import {
	addSnippetLoader,
	removeSnippetLoaderAndInsert,
} from '../../pm-plugins/loaderTransactions';
import {
	copySnippetMediaForInsertion,
	getSnippetMediaAttrs,
	hasSnippetMediaNodes,
} from '../../pm-plugins/snippetMedia';
import type { SnippetsPlugin, SnippetsPluginOptions } from '../../snippetsPluginType';

import { getSnippetQuickInsertKeywords } from './utils';

const SNIPPET_KEY_PREFIX = 'snippet-';

const messages = defineMessages({
	snippetBy: {
		// eslint-disable-next-line @atlassian/i18n/enforce-id-by-package -- Preserve the existing translation key after moving this implementation.
		id: 'editor-plugin-snippets.snippetsPlugin.snippetBy',
		defaultMessage: 'by {name}',
		description: 'Description text showing who created the snippet, with the creator name',
	},
});

const getSnippetDescription = (
	snippet: Snippet,
	intl?: {
		formatMessage: (
			descriptor: MessageDescriptor,
			values?: Record<string, string | number | boolean | Date>,
		) => string;
	},
): string | undefined => {
	if (snippet.description) {
		return snippet.description;
	}

	if (!intl) {
		logException(new Error('Missing intl for snippet quick insert description'), {
			location: 'editor-plugin-snippets/getSnippetDescription',
		});
		return `by ${snippet.createdBy}`;
	}

	return intl.formatMessage(messages.snippetBy, { name: snippet.createdBy });
};

const trackSnippetMediaPaste = (
	mediaAttrs: ReturnType<typeof getSnippetMediaAttrs>,
	api: Parameters<SnippetsPlugin>[0]['api'],
): void => {
	mediaAttrs.forEach((attrs) => {
		if (!api?.media?.commands?.trackMediaPaste) {
			return;
		}

		api.core.actions.execute(api.media.commands.trackMediaPaste(attrs));
	});
};

interface InsertSnippetFromQuickInsertParams {
	adf: ADFEntity[];
	api: Parameters<SnippetsPlugin>[0]['api'];
	inputMethod?: InsertSnippetInputMethod;
	insert: QuickInsertActionInsert;
	onSnippetInserted?: SnippetsPluginOptions['onSnippetInserted'];
	snippet: Snippet;
	state: EditorState;
}

const insertSnippetFromQuickInsert = ({
	adf,
	api,
	inputMethod = INPUT_METHOD.QUICK_INSERT,
	insert,
	onSnippetInserted,
	snippet,
	state,
}: InsertSnippetFromQuickInsertParams): ReturnType<QuickInsertItem['action']> => {
	const fireAnalyticsEvent = api?.analytics?.actions?.fireAnalyticsEvent;
	const { snippetId, templateType } = snippet;
	const hasMedia = hasSnippetMediaNodes(adf);
	const notifySnippetInserted = (): void => {
		try {
			onSnippetInserted?.({ inputMethod, snippet });
		} catch (error) {
			logException(error as Error, {
				location: 'editor-plugin-snippets/insertSnippetContent/onSnippetInserted',
			});
		}
	};

	try {
		if (!hasMedia) {
			const fragment = Fragment.fromJSON(state.schema, adf);
			const result = insert(fragment);
			fireAnalyticsEvent?.({
				action: ACTION.INSERTED,
				actionSubject: ACTION_SUBJECT.DOCUMENT,
				actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
				attributes: {
					inputMethod,
					snippetId,
					templateType,
				},
				eventType: EVENT_TYPE.TRACK,
			});
			notifySnippetInserted();
			return result;
		}
	} catch (error) {
		fireAnalyticsEvent?.({
			action: ACTION.FAILED_TO_INSERT,
			actionSubject: ACTION_SUBJECT.DOCUMENT,
			actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
			attributes: { reason: 'parseError', snippetId, templateType },
			eventType: EVENT_TYPE.OPERATIONAL,
		});
		logException(error as Error, {
			location: 'editor-plugin-snippets/insertSnippetContent',
		});
		return false;
	}

	const dismissTr = insert('');
	const loaderId = crypto.randomUUID();
	const loaderPosAtAdd = addSnippetLoader({ api, dismissTr, loaderId });

	copySnippetMediaForInsertion(adf, {
		mediaProvider: api?.media?.sharedState.currentState()?.mediaProvider,
		editorContextId:
			api?.contextIdentifier?.sharedState.currentState()?.contextIdentifierProvider?.objectId,
	})
		.then(
			({
				status,
				adf: adfForInsertion,
				numTotalMediaFiles,
				numFailedMediaFiles,
				failedMediaAttrs,
			}) => {
				trackSnippetMediaPaste(failedMediaAttrs, api);

				try {
					removeSnippetLoaderAndInsert({ api, loaderId, loaderPosAtAdd, adf: adfForInsertion });
				} catch (error) {
					fireAnalyticsEvent?.({
						action: ACTION.FAILED_TO_INSERT,
						actionSubject: ACTION_SUBJECT.DOCUMENT,
						actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
						attributes: { reason: 'parseError', snippetId, templateType },
						eventType: EVENT_TYPE.OPERATIONAL,
					});
					logException(error as Error, {
						location: 'editor-plugin-snippets/insertSnippetContent',
					});
					return dismissTr;
				}

				fireAnalyticsEvent?.({
					action: ACTION.INSERTED,
					actionSubject: ACTION_SUBJECT.DOCUMENT,
					actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
					attributes: {
						inputMethod,
						snippetId,
						templateType,
						hadMedia: true,
					},
					eventType: EVENT_TYPE.TRACK,
				});
				notifySnippetInserted();
				if (status !== 'copied' && numFailedMediaFiles > 0) {
					fireAnalyticsEvent?.({
						action: ACTION.FAILED_TO_INSERT,
						actionSubject: ACTION_SUBJECT.DOCUMENT,
						actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
						attributes: {
							reason: 'snippetMediaDirectCopyFailed',
							snippetMediaCopyStatus: status,
							snippetId,
							templateType,
							numTotalMediaFiles,
							numFailedMediaFiles,
						},
						eventType: EVENT_TYPE.OPERATIONAL,
					});
					logException(new Error('Partial media copy intent failure'), {
						location: 'editor-plugin-snippets/insertSnippetContent',
					});
				}
			},
		)
		.catch((error) => {
			const mediaAttrs = getSnippetMediaAttrs(adf);
			trackSnippetMediaPaste(mediaAttrs, api);
			fireAnalyticsEvent?.({
				action: ACTION.FAILED_TO_INSERT,
				actionSubject: ACTION_SUBJECT.DOCUMENT,
				actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
				attributes: {
					reason: 'snippetMediaDirectCopyUnexpectedError',
					snippetId,
					templateType,
					numTotalMediaFiles: mediaAttrs.length,
					numFailedMediaFiles: mediaAttrs.length,
				},
				eventType: EVENT_TYPE.OPERATIONAL,
			});
			logException(error as Error, {
				location: 'editor-plugin-snippets/insertSnippetContent',
			});

			try {
				removeSnippetLoaderAndInsert({ api, loaderId, loaderPosAtAdd, adf });
			} catch {
				fireAnalyticsEvent?.({
					action: ACTION.FAILED_TO_INSERT,
					actionSubject: ACTION_SUBJECT.DOCUMENT,
					actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
					attributes: { reason: 'parseError', snippetId, templateType },
					eventType: EVENT_TYPE.OPERATIONAL,
				});
				return false;
			}

			fireAnalyticsEvent?.({
				action: ACTION.INSERTED,
				actionSubject: ACTION_SUBJECT.DOCUMENT,
				actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
				attributes: { hadMedia: true, inputMethod, snippetId, templateType },
				eventType: EVENT_TYPE.TRACK,
			});
			if (onSnippetInserted) {
				notifySnippetInserted();
			}
		});

	return dismissTr;
};

const isInsertSnippetInputMethod = (
	inputMethod: INPUT_METHOD,
): inputMethod is InsertSnippetInputMethod =>
	inputMethod === INPUT_METHOD.ELEMENT_BROWSER ||
	inputMethod === INPUT_METHOD.INSERT_MENU ||
	inputMethod === INPUT_METHOD.QUICK_INSERT ||
	inputMethod === INPUT_METHOD.TOOLBAR;

export const mapToQuickInsertItem = (
	snippet: Snippet,
	api?: Parameters<SnippetsPlugin>[0]['api'],
	intl?: {
		formatMessage: (
			descriptor: MessageDescriptor,
			values?: Record<string, string | number | boolean | Date>,
		) => string;
	},
): QuickInsertItem => ({
	key: `${SNIPPET_KEY_PREFIX}${snippet.snippetId}`,
	title: snippet.name,
	description: getSnippetDescription(snippet, intl),
	keywords: getSnippetQuickInsertKeywords(intl),
	isDisabledOffline: true,
	icon: () => <TemplateIcon label="Snippet" />,
	action: (insert: QuickInsertActionInsert, state, source = INPUT_METHOD.QUICK_INSERT) => {
		if (!isExperimentEnabled('platform_editor_snippets_plugin_confluence')) {
			const { snippetId, templateType } = snippet;
			const adf = snippet.body.content?.filter((node) => node !== undefined);
			if (!adf || adf.length === 0) {
				api?.analytics?.actions?.fireAnalyticsEvent?.({
					action: ACTION.FAILED_TO_INSERT,
					actionSubject: ACTION_SUBJECT.DOCUMENT,
					actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
					attributes: { reason: 'emptyBody', snippetId, templateType },
					eventType: EVENT_TYPE.OPERATIONAL,
				});
				logException(new Error('Empty snippet body'), {
					location: 'editor-plugin-snippets/mapToQuickInsertItem',
				});
				return false;
			}

			return insertSnippetFromQuickInsert({
				adf,
				api,
				insert,
				snippet,
				state,
			});
		}

		const inputMethod = isInsertSnippetInputMethod(source) ? source : INPUT_METHOD.QUICK_INSERT;
		if (!api?.snippets?.commands?.insertSnippet) {
			const { snippetId, templateType } = snippet;
			const adf = snippet.body.content?.filter((node) => node !== undefined);
			if (!adf || adf.length === 0) {
				api?.analytics?.actions?.fireAnalyticsEvent?.({
					action: ACTION.FAILED_TO_INSERT,
					actionSubject: ACTION_SUBJECT.DOCUMENT,
					actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
					attributes: { reason: 'emptyBody', snippetId, templateType },
					eventType: EVENT_TYPE.OPERATIONAL,
				});
				logException(new Error('Empty snippet body'), {
					location: 'editor-plugin-snippets/mapToQuickInsertItem',
				});
				return false;
			}

			return insertSnippetFromQuickInsert({
				adf,
				api,
				inputMethod,
				insert,
				snippet,
				state,
			});
		}
		const dismissTr = insert('');
		if (!dismissTr || typeof dismissTr !== 'object') {
			return false;
		}
		return (
			api.snippets.commands.insertSnippet({ inputMethod, snippet })({
				tr: dismissTr as Transaction,
			}) ?? false
		);
	},
});
